import { Box, Drawer, Typography, IconButton, useTheme } from '@mui/material';
import { IoClose } from 'react-icons/io5';
import Input from 'components/Common/Input';
import Radio from 'components/Common/Radio';
import Select from 'components/Common/Select';
import { useForm, useWatch } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useAcceptRecommendation } from '@/apis/business/agent/performance/hook';
import { useGetKnowledgeBases } from '@/apis/business/setting/knowledgeBase/hook';
import { useMemo } from 'react';

type FormValues = {
  id: string;
  question: string;
  answer: string;
  description?: string;
  destination_type: 'agent' | 'business';
  knowledge_base_id?: number;
};

export default function AddQAForm({
  defaultValues,
  open,
  onClose
}: {
  defaultValues: { id: string; question: string; description?: string } | null;
  open: boolean;
  onClose: any;
}) {
  const theme = useTheme();
  const { mutateAsync: acceptRecommendation, isPending } = useAcceptRecommendation(onClose);
  const { data: knowledgeBases } = useGetKnowledgeBases(null);

  const formValues = useMemo<FormValues>(
    () => ({
      id: defaultValues?.id ?? '',
      question: defaultValues?.question ?? '',
      description: defaultValues?.description,
      answer: '',
      destination_type: 'agent',
      knowledge_base_id: undefined
    }),
    [defaultValues]
  );

  const { handleSubmit, control } = useForm<FormValues>({
    values: formValues
  });

  const destinationType = useWatch({ control, name: 'destination_type' });

  const knowledgeBaseOptions = useMemo(
    () =>
      (knowledgeBases?.data ?? []).map((kb) => ({
        value: kb.id,
        label: kb.name
      })),
    [knowledgeBases]
  );

  const onSubmit = async (data: FormValues) => {
    await acceptRecommendation({
      id: data.id,
      question: data.question,
      answer: data.answer,
      destination_type: data.destination_type,
      ...(data.destination_type === 'business' && data.knowledge_base_id
        ? { knowledge_base_id: data.knowledge_base_id }
        : {})
    });
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={() => onClose()}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400, md: 600 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100001
        }
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #DEDEDE',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography variant='h6'>Add to Knowledge Base</Typography>
        <IconButton onClick={() => onClose()} size='small'>
          <IoClose size={24} />
        </IconButton>
      </Box>

      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3
          }}
        >
          {defaultValues?.description && (
            <Box bgcolor={theme.palette.warning.light} borderRadius={2} px={2} py={1.5} mb={2}>
              <Typography fontSize={14} color={theme.palette.warning.main}>
                Context: {defaultValues.description}
              </Typography>
            </Box>
          )}

          <Typography fontWeight={500} fontSize={16}>
            Save Recommendation To
          </Typography>

          <Radio
            name='destination_type'
            control={control}
            options={[
              { label: 'Agent Knowledge Base', value: 'agent' },
              { label: 'Business Knowledge Base', value: 'business' }
            ]}
          />

          {destinationType === 'business' && (
            <Box mt={2}>
              <Select
                name='knowledge_base_id'
                label='Knowledge Base'
                control={control}
                options={knowledgeBaseOptions}
                fullWidth
                required
              />
            </Box>
          )}

          <Box mt={2}>
            <Input name='question' label='Question' control={control} fullWidth disabled />
          </Box>
          <Box mt={2}>
            <Input
              name='answer'
              label='Your Answer'
              placeholder='Type the answer AI should give when this comes up...'
              control={control}
              minRows={6}
              multiline
              fullWidth
              required
            />
          </Box>
        </Box>

        {/* Fixed Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #DEDEDE',
            display: 'flex',
            justifyContent: 'end',
            gap: 2,
            flexShrink: 0
          }}
        >
          <Button color='secondary' variant='outlined' sx={{ border: '1px solid #F3F3F3' }} onClick={() => onClose()}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isPending}>
            Create
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
